import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MapPin, Star, Users, Search, Sparkles, SlidersHorizontal, ArrowUpDown } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";

const cities = ["All Cities", "Bangalore", "Mumbai", "Delhi", "Hyderabad", "Pune", "Goa", "Chennai", "Kolkata", "Jaipur", "Ahmedabad"];
const vibeFilters = ["rooftop", "live-music", "dj", "craft-beer", "vip", "casual", "premium", "cocktail-bar"];
const sortOptions = [
  { value: "rating", label: "Top Rated" },
  { value: "price-low", label: "Price: Low → High" },
  { value: "price-high", label: "Price: High → Low" },
  { value: "popular", label: "Most Popular" },
];

const VenueCardSkeleton = () => (
  <div className="overflow-hidden rounded-2xl border border-border bg-card">
    <Skeleton className="aspect-video w-full" />
    <div className="p-5 space-y-3">
      <div className="flex justify-between"><Skeleton className="h-5 w-32" /><Skeleton className="h-5 w-12" /></div>
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-4 w-full" />
      <div className="flex justify-between"><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-16" /></div>
    </div>
  </div>
);

const Venues = () => {
  const { profile } = useAuth();
  const [venues, setVenues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedVibe, setSelectedVibe] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState(profile?.city || "All Cities");
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [sortBy, setSortBy] = useState("rating");
  const [showFilters, setShowFilters] = useState(false);
  const [aiPicks, setAiPicks] = useState<any[] | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    const fetchVenues = async () => {
      let query = supabase.from("venues").select("*").eq("is_active", true);
      if (selectedCity !== "All Cities") query = query.eq("city", selectedCity);
      const { data } = await query.order("rating", { ascending: false });
      if (data) setVenues(data);
      setLoading(false);
    };
    fetchVenues();
  }, [selectedCity]);

  const fetchAiPicks = async () => {
    setAiLoading(true);
    setAiPicks(null);
    try {
      const { data, error } = await supabase.functions.invoke("ai-venue-recommend", {
        body: {
          vibes: profile?.bio ? [] : ["dj", "premium"],
          budget: 3000,
          groupSize: 4,
          city: selectedCity === "All Cities" ? undefined : selectedCity,
        },
      });
      if (error) throw error;
      setAiPicks(data.venues || []);
    } catch (e) {
      console.error("AI picks error:", e);
    }
    setAiLoading(false);
  };

  const filtered = venues
    .filter((v) => {
      const matchesSearch = v.name.toLowerCase().includes(search.toLowerCase()) ||
        v.area.toLowerCase().includes(search.toLowerCase());
      const matchesVibe = !selectedVibe || (v.vibe && v.vibe.includes(selectedVibe));
      const matchesPrice = v.avg_price_per_person >= priceRange[0] && v.avg_price_per_person <= priceRange[1];
      return matchesSearch && matchesVibe && matchesPrice;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "price-low": return a.avg_price_per_person - b.avg_price_per_person;
        case "price-high": return b.avg_price_per_person - a.avg_price_per_person;
        case "popular": return (b.total_ratings || 0) - (a.total_ratings || 0);
        default: return (b.rating || 0) - (a.rating || 0);
      }
    });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Explore <span className="text-gradient-neon">Venues</span></h1>
          <p className="mt-1 text-muted-foreground font-body">Curated nightlife spots for your Krew</p>
        </div>
        <button
          onClick={fetchAiPicks}
          disabled={aiLoading}
          className="flex items-center gap-2 rounded-xl bg-primary/10 px-4 py-2.5 text-sm font-semibold text-primary border border-primary/30 hover:bg-primary/20 transition-all disabled:opacity-50"
        >
          <Sparkles className="h-4 w-4" />
          {aiLoading ? "Finding..." : "AI Pick for Tonight"}
        </button>
      </div>

      {/* AI Picks */}
      {aiPicks && aiPicks.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-bold text-foreground">AI Picks for Tonight</h2>
            <button onClick={() => setAiPicks(null)} className="ml-auto text-xs text-muted-foreground hover:text-foreground">Dismiss</button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {aiPicks.slice(0, 3).map((v: any) => (
              <Link key={v.id} to={`/venue/${v.slug}`} className="group rounded-2xl border border-primary/20 bg-primary/5 p-4 transition-all hover:border-primary/40">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-bold text-foreground">{v.name}</h3>
                  <span className="rounded-full bg-primary/20 px-2.5 py-0.5 text-xs font-bold text-primary">{v.match_score}% match</span>
                </div>
                <p className="mb-2 text-xs text-muted-foreground">{v.area}, {v.city} · ₹{v.avg_price_per_person}/person</p>
                <p className="text-xs text-primary/80 italic">{v.ai_match_reason}</p>
              </Link>
            ))}
          </div>
        </motion.section>
      )}

      {/* City Selector */}
      <div className="flex flex-wrap gap-2">
        {cities.map(city => (
          <button
            key={city}
            onClick={() => { setSelectedCity(city); setLoading(true); }}
            className={`rounded-full px-4 py-2 text-xs font-medium transition-all ${
              selectedCity === city ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {city}
          </button>
        ))}
      </div>

      {/* Search + Filters Toggle */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search venues or areas..."
            className="w-full rounded-xl border border-border bg-card pl-11 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm transition-all ${
            showFilters ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground hover:text-foreground"
          }`}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
        </button>
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-4 rounded-xl border border-border bg-card p-5">
          {/* Price Range */}
          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">
              Price Range: ₹{priceRange[0].toLocaleString()} — ₹{priceRange[1].toLocaleString()}
            </label>
            <Slider
              min={0} max={10000} step={500}
              value={priceRange}
              onValueChange={setPriceRange}
            />
          </div>
          {/* Sort */}
          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">Sort By</label>
            <div className="flex flex-wrap gap-2">
              {sortOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                    sortBy === opt.value ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <ArrowUpDown className="h-3 w-3" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Vibe Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedVibe(null)}
          className={`rounded-full px-4 py-2 text-xs font-medium transition-all ${
            !selectedVibe ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
          }`}
        >All</button>
        {vibeFilters.map((vibe) => (
          <button
            key={vibe}
            onClick={() => setSelectedVibe(vibe === selectedVibe ? null : vibe)}
            className={`rounded-full px-4 py-2 text-xs font-medium transition-all ${
              selectedVibe === vibe ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >{vibe}</button>
        ))}
      </div>

      {/* Results count */}
      <p className="text-xs text-muted-foreground">{filtered.length} venue{filtered.length !== 1 ? "s" : ""} found</p>

      {/* Venues Grid */}
      {loading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => <VenueCardSkeleton key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((venue, i) => (
            <motion.div key={venue.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Link
                to={`/venue/${venue.slug}`}
                className="group block overflow-hidden rounded-2xl border border-border bg-card transition-all hover:border-primary/30 hover:shadow-lg"
              >
                <div className="aspect-video overflow-hidden">
                  <img src={venue.image_url || "https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=600"} alt={venue.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                </div>
                <div className="p-5">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-foreground">{venue.name}</h3>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-secondary" />
                      <span className="text-sm font-semibold text-foreground">{venue.rating}</span>
                      {venue.total_ratings > 0 && <span className="text-xs text-muted-foreground">({venue.total_ratings})</span>}
                    </div>
                  </div>
                  <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />{venue.area}, {venue.city}
                  </div>
                  <p className="mb-4 text-sm text-muted-foreground line-clamp-2">{venue.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gradient-gold">₹{venue.avg_price_per_person}/person</span>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="h-3.5 w-3.5" />{venue.capacity}
                    </div>
                  </div>
                  {venue.vibe && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {venue.vibe.slice(0, 3).map((v: string) => (
                        <span key={v} className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">{v}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="py-20 text-center text-muted-foreground">No venues found matching your criteria</div>
      )}
    </div>
  );
};

export default Venues;
